import { useI18n } from "../I18nContext";

const JS_DAYS = [0, 1, 2, 3, 4, 5, 6];
const NTH_KEYS = { 1: "first", 2: "second", 3: "third", 4: "fourth", "-1": "last" };

export const EMPTY_RECURRENCE = {
  recurrence: "",
  recurrence_freq: "weekly",
  recurrence_interval: 1,
  recurrence_weekdays: [],
  recurrence_monthly: "byweekday",
  recurrence_end: "count",
  recurrence_count: 4,
  recurrence_until: "",
};

function parseWhen(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function weekdayNth(d) {
  const n = Math.floor((d.getDate() - 1) / 7) + 1;
  return n >= 5 ? -1 : n;
}

function jsToIso(day) {
  return (Number(day) + 6) % 7;
}

export function appendRecurrence(payload, form) {
  let kind = form.recurrence;
  if (!kind) return payload;
  if (kind === "custom") kind = form.recurrence_freq || "weekly";
  payload.recurrence = kind;
  payload.recurrence_interval = Math.max(1, Number(form.recurrence_interval) || 1);
  if (form.recurrence_end === "until" && form.recurrence_until) {
    payload.recurrence_until = new Date(`${form.recurrence_until}T23:59:59`).toISOString();
    payload.recurrence_count = 12;
  } else {
    payload.recurrence_count = Math.max(2, Math.min(12, Number(form.recurrence_count) || 4));
  }
  const jsDays = form.recurrence_weekdays || [];
  if (kind === "weekly" && jsDays.length) {
    payload.recurrence_weekdays = jsDays.map(jsToIso);
  }
  if (kind === "monthly") {
    payload.recurrence_monthly = form.recurrence_monthly || "byweekday";
  }
  return payload;
}

export default function RecurrenceFields({ form, setForm }) {
  const { t, lang } = useI18n();
  const when = parseWhen(form.scheduled_at);
  const locale = lang === "fr" ? "fr-CA" : "en-CA";
  const dayName = when
    ? when.toLocaleDateString(locale, { weekday: "long" })
    : t("book.dowPlaceholder");
  const nthKey = when ? NTH_KEYS[String(weekdayNth(when))] : "first";
  const monthDay = when ? when.getDate() : 1;
  const yearlyDate = when
    ? when.toLocaleDateString(locale, { month: "long", day: "numeric" })
    : t("book.dowPlaceholder");

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const onKind = (value) => {
    const patch = { recurrence: value };
    if (value === "custom") {
      patch.recurrence_freq = form.recurrence_freq || "weekly";
      if (!form.recurrence_weekdays?.length && when) {
        patch.recurrence_weekdays = [when.getDay()];
      }
    }
    if (value === "monthly") patch.recurrence_monthly = "byweekday";
    set(patch);
  };

  const toggleDay = (day) => {
    const cur = form.recurrence_weekdays || [];
    const next = cur.includes(day) ? cur.filter((d) => d !== day) : [...cur, day].sort();
    set({ recurrence_weekdays: next.length ? next : when ? [when.getDay()] : [1] });
  };

  const showCount = form.recurrence && form.recurrence !== "custom" && form.recurrence_end !== "until";
  const custom = form.recurrence === "custom";
  const freq = form.recurrence_freq || "weekly";
  const interval = Number(form.recurrence_interval) || 1;

  return (
    <>
      <div className="tb-row">
        <div>
          <label>{t("book.recurrence")}</label>
          <select value={form.recurrence} onChange={(e) => onKind(e.target.value)} data-testid="select-recurrence">
            <option value="">{t("book.recurrenceNone")}</option>
            <option value="daily">{t("book.recurrenceDaily")}</option>
            <option value="weekly">{t("book.recurrenceWeeklyOn", { day: dayName })}</option>
            <option value="monthly">{t("book.recurrenceMonthlyOnNth", { nth: t(`book.nth.${nthKey}`), day: dayName })}</option>
            <option value="yearly">{t("book.recurrenceYearlyOn", { date: yearlyDate })}</option>
            <option value="weekdays">{t("book.recurrenceWeekdays")}</option>
            <option value="custom">{t("book.recurrenceCustom")}</option>
          </select>
        </div>
        {showCount && (
          <div>
            <label>{t("book.recurrenceCount")}</label>
            <input
              type="number"
              min="2"
              max="12"
              value={form.recurrence_count}
              onChange={(e) => set({ recurrence_count: e.target.value })}
              data-testid="input-recurrence-count"
            />
          </div>
        )}
      </div>
      {custom && (
        <div className="tb-block tb-recur-custom" data-testid="recurrence-custom">
          <div className="tb-row">
            <div>
              <label>{t("book.recurrenceEvery")}</label>
              <div className="tb-recur-every">
                <input
                  type="number"
                  min="1"
                  max="30"
                  value={form.recurrence_interval}
                  onChange={(e) => set({ recurrence_interval: e.target.value })}
                  data-testid="input-recurrence-interval"
                />
                <select
                  value={freq}
                  onChange={(e) => set({ recurrence_freq: e.target.value })}
                  data-testid="select-recurrence-freq"
                >
                  <option value="daily">{t(interval === 1 ? "book.recurrenceUnitDay" : "book.recurrenceUnitDays")}</option>
                  <option value="weekly">{t(interval === 1 ? "book.recurrenceUnitWeek" : "book.recurrenceUnitWeeks")}</option>
                  <option value="monthly">{t(interval === 1 ? "book.recurrenceUnitMonth" : "book.recurrenceUnitMonths")}</option>
                  <option value="yearly">{t(interval === 1 ? "book.recurrenceUnitYear" : "book.recurrenceUnitYears")}</option>
                </select>
              </div>
            </div>
          </div>
          {freq === "weekly" && (
            <div>
              <label>{t("book.recurrenceOnDays")}</label>
              <div className="tb-recur-days">
                {JS_DAYS.map((d) => (
                  <button
                    key={d}
                    type="button"
                    className={`tb-recur-day${(form.recurrence_weekdays || []).includes(d) ? " on" : ""}`}
                    onClick={() => toggleDay(d)}
                    data-testid={`recur-day-${d}`}
                    aria-pressed={(form.recurrence_weekdays || []).includes(d)}
                  >
                    {t(`book.dowShort.${d}`)}
                  </button>
                ))}
              </div>
            </div>
          )}
          {freq === "monthly" && (
            <div>
              <label>{t("book.recurrenceMonthlyMode")}</label>
              <select
                value={form.recurrence_monthly}
                onChange={(e) => set({ recurrence_monthly: e.target.value })}
                data-testid="select-recurrence-monthly"
              >
                <option value="byweekday">
                  {t("book.recurrenceMonthlyOnNth", { nth: t(`book.nth.${nthKey}`), day: dayName })}
                </option>
                <option value="bymonthday">
                  {t("book.recurrenceMonthlyOnDay", { n: monthDay })}
                </option>
              </select>
            </div>
          )}
          <div className="tb-row">
            <div>
              <label>{t("book.recurrenceEnds")}</label>
              <select
                value={form.recurrence_end}
                onChange={(e) => set({ recurrence_end: e.target.value })}
                data-testid="select-recurrence-end"
              >
                <option value="count">{t("book.recurrenceEndsAfter")}</option>
                <option value="until">{t("book.recurrenceEndsOn")}</option>
              </select>
            </div>
            {form.recurrence_end === "until" ? (
              <div>
                <label>{t("book.recurrenceUntil")}</label>
                <input
                  type="date"
                  required
                  value={form.recurrence_until}
                  onChange={(e) => set({ recurrence_until: e.target.value })}
                  data-testid="input-recurrence-until"
                />
              </div>
            ) : (
              <div>
                <label>{t("book.recurrenceCount")}</label>
                <input
                  type="number"
                  min="2"
                  max="12"
                  value={form.recurrence_count}
                  onChange={(e) => set({ recurrence_count: e.target.value })}
                  data-testid="input-recurrence-count"
                />
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
